import { db, auth, FirebaseFieldValue, FirebaseTimestamp } from '@/js/external-js/config/main_config';
import { getValidToken } from '@/js/external-js/variableModules/utils/getValidToken.util';
import { deiterateAPI } from '@/js/external-js/variableModules/utils/deiterateAPI.util';
import SparkMD5 from 'spark-md5';

// Types
export interface ControlData {
  controlID: string;
  controlData: {
    controlID: string;
    currentImplementationStatus?: 'Fully Implemented' | 'Partially Implemented' | 'Not Implemented' | 'Not Applicable';
    implementationStatus?: 'Fully Implemented' | 'Partially Implemented' | 'Not Implemented' | 'Not Applicable';
    status?: 'Fully Implemented' | 'Partially Implemented' | 'Not Implemented' | 'Not Applicable';
    title?: string;
    description?: string;
    // Add other fields from controlData as needed
    [key: string]: any; // Keep this for backward compatibility
  };
  // Add status fields at the root level as well for backward compatibility
  currentImplementationStatus?: 'Fully Implemented' | 'Partially Implemented' | 'Not Implemented' | 'Not Applicable';
  implementationStatus?: 'Fully Implemented' | 'Partially Implemented' | 'Not Implemented' | 'Not Applicable';
  status?: 'Fully Implemented' | 'Partially Implemented' | 'Not Implemented' | 'Not Applicable';
}

export interface AssuranceTask {
  assID: string;
  checkDetail: {
    assignedTo: string;
    check: string;
    procRef?: string[];
  };
  checkFrequency: {
    nextCheck: any; // Firebase timestamp
  };
  controlID?: string[];
}

export interface SupportingDoc {
  docID: string;
  fileName: string;
  docType: string;
  nextReviewDate: any; // Firebase timestamp
  owner: string;
  procRef?: string[];
}

export interface SOAUpdatesData {
  controls: ControlData[];
  processedControls: string[];
  hasMoreControls: boolean;
  currentBatch: number;
}

// Helper function to sort object keys for consistent hashing
export const sortObjectKeys = (obj: any): any => {
  if (Array.isArray(obj)) {
    return obj.map(sortObjectKeys);
  } else if (obj !== null && typeof obj === 'object') {
    return Object.keys(obj)
      .sort()
      .reduce((result: any, key) => {
        result[key] = sortObjectKeys(obj[key]);
        return result;
      }, {});
  }
  return obj;
};

// Get the list of controls with changelog updates
export const getSOAUpdatesData = async (
  customerID: string,
  currentBatch = 0,
  batchSize = 5
): Promise<SOAUpdatesData> => {
  try {
    // Initial data structure
    const result: SOAUpdatesData = {
      controls: [],
      processedControls: [],
      hasMoreControls: false,
      currentBatch: currentBatch,
    };
    
    // Get the known hashes to avoid re-processing
    const companyRef = db.collection('customers').doc(customerID);
    const companyDoc = await companyRef.get();
    const knownHashes = companyDoc.data()?.changelogHashes || [];
    
    // Query to get documents where the 'changelog' field is not empty
    const controlsRef = db.collection('/compliance/ism/controls');
    const controlsQuery = controlsRef.where('changelog', '!=', []);
    const controlsSnapshot = await controlsQuery.get();
    
    if (controlsSnapshot.empty) {
      return result;
    }
    
    // Get all controls with changelog
    const allControls = [];
    
    // Process controls in batches to avoid too many concurrent requests
    const processingBatchSize = 20; // Process 20 controls at a time (internal processing batch)
    const totalControls = controlsSnapshot.docs.length;
    
    console.log(`[ControlsService] Processing ${totalControls} controls in batches of ${processingBatchSize}`);
    
    for (let i = 0; i < totalControls; i += processingBatchSize) {
      const batch = controlsSnapshot.docs.slice(i, i + processingBatchSize);
      const controlPromises = batch.map(async (doc) => {
        const controlID = doc.id;
        const controlData = doc.data();
        
        // Get the customer-specific implementation data
        const customerDocPath = `/customers/${customerID}/product/policyPackages/ISO27001keyDocuments/ismsSoa/ism/${controlID}`;
        const customerDocRef = db.doc(customerDocPath);
        const customerDocSnapshot = await customerDocRef.get();
        
        if (!customerDocSnapshot.exists) {
          return null; // Skip controls that don't exist in customer's collection
        }
        
        const customerData = customerDocSnapshot.data() || {};
        
        // Merge the control data with customer-specific data
        return {
          controlID,
          ...customerData, // This will include implementationStatus, currentImplementationStatus, etc.
          controlData: {
            ...controlData,
            // Override with customer-specific values if they exist
            ...customerData
          }
        };
      });
      
      // Wait for all controls in the current batch to be processed
      const batchResults = await Promise.all(controlPromises);
      allControls.push(...batchResults.filter(Boolean));
    }
    
    // Calculate the start index for the current batch
    const start = currentBatch * batchSize;
    const batch = [];
    
    // Continue until we have a full batch or run out of controls to check
    let index = start;
    while (batch.length < batchSize && index < allControls.length) {
      const control = allControls[index];
      index++;
      
      // Check if the control has already been processed by comparing hashes
      const controlChangelog = control.controlData.changelog || [];
      // Use string-based hash for changelog tracking
      const changelogString = JSON.stringify(sortObjectKeys(controlChangelog));
      // Generate a hash using spark-md5
      const changelogHash = SparkMD5.hash(changelogString);
      
      if (!knownHashes.includes(changelogHash)) {
        batch.push(control);
      } else {
        result.processedControls.push(control.controlID);
      }
    }
    
    // Update the result
    result.controls = batch;
    result.hasMoreControls = index < allControls.length;
    result.currentBatch = currentBatch;
    
    return result;
  } catch (error) {
    console.error('Error fetching SOA updates data:', error);
    throw error;
  }
};

// Process a control by updating the known hashes
// Use spark-md5 for hashing
export const processControl = async (
  customerID: string, 
  controlID: string,
  changelogData: string // This is the stringified changelog data
): Promise<boolean> => {
  try {
    // Generate MD5 hash from the stringified data using spark-md5
    const changelogHash = SparkMD5.hash(changelogData);
    
    // Add the hash to the customer's document
    await db.collection('customers').doc(customerID).update({
      changelogHashes: FirebaseFieldValue.arrayUnion(changelogHash)
    });
    
    return true;
  } catch (error) {
    console.error(`Error processing control ${controlID}:`, error);
    return false;
  }
};

// Get assurance tasks for a specific control
export const loadAssuranceTasks = async (
  customerID: string,
  controlID: string
): Promise<AssuranceTask[]> => {
  console.log('[soaUpdatesService] loadAssuranceTasks via Thalassa API');
  try {
    const res = await deiterateAPI.GET(`/v1/soa/assurance-tasks/${controlID}`, {
      mode: 'cors',
      backend: 'thalassa',
      headers: { 'X-Tenancy-Id': customerID, 'Company-ID': customerID }
    });
    return res.tasks || [];
  } catch (error) {
    console.error('[soaUpdatesService] Error fetching assurance tasks:', error);
    return [];
  }
};

// Get supporting documents for a specific control
export const getSupportingDocs = async (
  customerID: string,
  controlID: string
): Promise<SupportingDoc[]> => {
  console.log('[soaUpdatesService] getSupportingDocs via Thalassa API');
  try {
    const res = await deiterateAPI.GET(`/v1/soa/supporting-docs/${controlID}`, {
      mode: 'cors',
      backend: 'thalassa',
      headers: { 'X-Tenancy-Id': customerID, 'Company-ID': customerID }
    });
    return res.docs || [];
  } catch (error) {
    console.error('[soaUpdatesService] Error fetching supporting documents:', error);
    return [];
  }
};

// Get users (assignees) for the customer
export const getAssignees = async (customerID: string): Promise<any[]> => {
  // Check if we're in a development environment
  const isDevelopment = window.location.hostname.includes('localhost') || window.location.hostname.includes("127.0.0.1");
  
  try {
    // Determine the fetch URL based on environment
    const fetchURL = isDevelopment
      ? 'http://localhost:5001/dev-deiterate/australia-southeast1'
      : import.meta.env.VITE_cloudFunctionURL;
    
    // Get a valid Firebase token
    const idToken = await getValidToken(auth);
    
    // In development, add timeout to prevent long hanging requests
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), isDevelopment ? 2000 : 10000);
    
    try {
      // Fetch the assignees from the API
      const response = await fetch(`${fetchURL}/deiterateInternalAPI/assurance/getAssignee`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
          'Company-ID': customerID,
        },
        body: JSON.stringify({
          customerID: customerID,
          mspID: sessionStorage.getItem('web-mspID'),
          msp: false, // Default to false, can be updated based on user roles if needed
        }),
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      const result = await response.json();
      return result.assignees || [];
    } catch (fetchError) {
      clearTimeout(timeoutId);
      if (isDevelopment) {
        console.warn('API connection failed, using fallback data for assignees in development mode', fetchError);
        // Return empty array for development, component will use TenancyContext users
        return [];
      }
      throw fetchError; // Re-throw for production to be caught by outer try/catch
    }
  } catch (error) {
    console.error('Error fetching assignees:', error);
    // Return empty array, components should handle this gracefully
    return [];
  }
};

// Get all SOA controls for a customer (used by the unified table)
export const getAllSOAControls = async (
  customerID: string,
  maxControls: number = 200,
  filterCompletedControls: boolean = true
): Promise<ControlData[]> => {
  try {
    console.log(`[ControlsService] Fetching up to ${maxControls} controls, filterCompleted: ${filterCompletedControls}`);
    
    // Get all controls from the compliance collection
    const controlsRef = db.collection('/compliance/ism/controls');
    const controlsQuery = controlsRef.where('changelog', '!=', []);
    const controlsSnapshot = await controlsQuery.get();
    console.log(`[ControlsService] Total controls found: ${controlsSnapshot.size}`);
    
    if (controlsSnapshot.empty) {
      console.log(`[ControlsService] No controls found`);
      return [];
    }

    // Process controls in batches to avoid too many concurrent requests
    const processingBatchSize = 20;
    const totalControls = controlsSnapshot.docs.length;
    const allControls: ControlData[] = [];
    
    console.log(`[ControlsService] Processing ${totalControls} controls in batches of ${processingBatchSize}`);
    
    // Process all controls in batches
    for (let i = 0; i < totalControls; i += processingBatchSize) {
      const batch = controlsSnapshot.docs.slice(i, i + processingBatchSize);
      const batchIndex = Math.floor(i / processingBatchSize);
      console.log(`[ControlsService] Starting batch ${batchIndex}...`);
      
      const controlPromises = batch.map(async (doc) => {
        const controlID = doc.id;
        const controlData = doc.data();
        
        try {
          // Get the customer-specific implementation data
          const customerDocPath = `/customers/${customerID}/product/policyPackages/ISO27001keyDocuments/ismsSoa/ism/${controlID}`;
          const customerDocRef = db.doc(customerDocPath);
          const customerDocSnapshot = await customerDocRef.get();
          
          if (!customerDocSnapshot.exists) {
            // Instead of skipping, return control with default customer data
            console.log(`[ControlsService] Customer doc doesn't exist for control ${controlID}, using default data`);
            return {
              controlID,
              implementationStatus: 'Not Implemented',
              currentImplementationStatus: 'Not Implemented',
              controlData: controlData
            } as ControlData;
          }
          
          const customerData = customerDocSnapshot.data() || {};
          
          // Merge the control data with customer-specific data
          return {
            controlID,
            ...customerData,
            controlData: {
              ...controlData,
              ...customerData
            }
          } as ControlData;
        } catch (error) {
          console.error(`[ControlsService] Error processing control ${controlID}:`, error);
          return null;
        }
      });
      
      // Wait for all controls in the current batch to be processed
      const batchResults = await Promise.all(controlPromises);
      const validControls = batchResults.filter(Boolean) as ControlData[];
      console.log(`[ControlsService] Processing batch ${batchIndex} with ${validControls.length} controls`);
      allControls.push(...validControls);
    }
    
    console.log(`[ControlsService] Fetched ${allControls.length} controls in total`);
    
    // If filtering is enabled, filter out fully implemented and not applicable controls
    let controlsToReturn = allControls;
    if (filterCompletedControls) {
      const initialCount = controlsToReturn.length;
      controlsToReturn = controlsToReturn.filter(control => {
        const status = control.currentImplementationStatus || control.implementationStatus || control.status;
        return status !== 'Fully Implemented' && status !== 'Not Applicable';
      });
      console.log(`[ControlsService] Filtered out ${initialCount - controlsToReturn.length} completed controls`);
    }
    
    if (controlsToReturn.length > maxControls) {
      controlsToReturn = controlsToReturn.slice(0, maxControls);
    }
    
    console.log(`[ControlsService] Returning ${controlsToReturn.length} controls`);
    
    return controlsToReturn;
  } catch (error) {
    console.error('Error in getAllSOAControls:', error);
    throw error;
  }
};

// Update a control in the customer's SOA
export const updateControlInSOA = async (
  customerID: string,
  controlID: string,
  updates: Record<string, any>
): Promise<boolean> => {
  try {
    const controlPath = `/customers/${customerID}/product/policyPackages/ISO27001keyDocuments/ismsSoa/ism/${controlID}`;
    await db.doc(controlPath).update(updates);
    return true;
  } catch (error) {
    console.error(`Error updating control ${controlID}:`, error);
    return false;
  }
};
